/**
 * /{ws}/mail/t/{threadId} — one conversation (core MailThreadViewModel), Gmail style: subject and actions (back,
 * archive, delete, spam, unread, star, the Priority marker, snooze, labels, move to tab), the phishing/auth warning, then the messages oldest first — collapsed
 * ones as a sender + snippet line, expanded ones with their sanitized HTML in a sandboxed frame (HtmlFrame: no
 * scripts, no same-origin; remote images blocked until "Load images"), attachments and Reply / Reply all / Forward.
 * Like Gmail, a long conversation folds the messages between the first and the last two behind a round count, a
 * plain-text message's quoted reply hides behind "•••", Reply / Reply all / Forward stay pinned under the scroll, and
 * replies are written in a box under the last message (full screen on phones).
 */
import type { MailMessage, MailThread, SendMailResponse } from "@bundle/contracts";
import type { IconName } from "@bundle/tokens";
import { Files, MailThreadViewModel, Shell, type ComposerStart, type MailBody } from "@bundle/core";
import * as Mail from "@bundle/core/logic/mail";
import {
  AnimatedList,
  Avatar,
  Banner,
  Button,
  Card,
  Chip,
  ComingSoon,
  Divider,
  FoldCount,
  HtmlFrame,
  Icon,
  IconButton,
  mailTabIcon,
  Menu,
  PersonName,
  Pressable,
  ThreadProjectChip,
  ScrollView,
  Spinner,
  Stack,
  Text,
  Tooltip,
  mailFileSize,
  mailShortDate,
  onMailEvent,
  labelTone,
  openComposer,
  showMailUndo,
  MailCodeCard,
  useFormat,
  useLocale,
  useMail,
  useMediaQuery,
  useNavigation,
  useOwned,
  useT,
  useViewModel,
  useWorkspace,
  useFeatureAccess,
  type MailServices,
  type MenuEntry,
} from "@bundle/ui";
import { useCallback, useEffect, useRef, useState } from "react";
import { useAttachmentDownloads, type AttachmentIntent, type AttachmentState, type DownloadableAttachment } from "./attachmentDownloads.tsx";
import { MailInviteCard, inviteWhen } from "./MailInviteCard.tsx";
import { MailReplyBox, QuoteToggle, SentToast } from "./MailComposer.tsx";
import { labelEntries, snoozeEntries } from "./mailMenus.ts";
import { PriorityMarker, markPriority, noReplyDays } from "./priority.tsx";
import { useMailShortcuts } from "./useMailShortcuts.ts";

type ReplyKind = "reply" | "replyAll" | "forward";
let replySeq = 0;

export function MailThreadScreen({ threadId }: { threadId: string }) {
  const t = useT();
  const mail = useMail();
  if (mail === undefined) return <ComingSoon icon="mail" title={t("mail.title")} body={t("mail.loadFailed")} />;
  return <ThreadView threadId={threadId} mail={mail} />;
}

function ThreadView({ threadId, mail }: { threadId: string; mail: MailServices }) {
  const downloads = useAttachmentDownloads(mail);
  const t = useT();
  const nav = useNavigation();
  const ws = useWorkspace();
  // Email switched off while owners and admins may still look (Settings → Features): no replying or forwarding.
  const canSend = useFeatureAccess()("mail") === "full";
  const create = useCallback(() => {
    const holder: { vm?: MailThreadViewModel } = {};
    const box = () => mail.mailbox.state.mailboxes.find((m) => m.id === holder.vm?.state.thread?.mailboxId);
    // Replies start from the address the message was sent to (Gmail), or always my default (You › Mail), and none of
    // my addresses is replied to.
    holder.vm = new MailThreadViewModel(ws.vm, threadId, () => box()?.address, () => box()?.sendAs, () => box()?.replyFrom);
    return holder.vm;
  }, [ws.vm, threadId, mail]);
  const vm = useOwned(`mailThread:${ws.vm.wsId}:${threadId}`, create);
  useEffect(() => {
    void vm.load();
    return onMailEvent((e) => vm.onEvent(e));
  }, [vm]);
  const s = useViewModel(vm);
  const thread = s.thread;
  const fmt = useFormat();
  const { labels, tabDefs } = useViewModel(mail.mailbox);
  // Beside the list (panes split, like a chat thread) it closes; alone it goes back to its folder.
  const back = () =>
    nav.canSplit
      ? nav.close()
      : nav.open(Shell.formatUrl(thread === undefined ? { type: "mail", ws: ws.slug } : { type: "mail", ws: ws.slug, folder: thread.folder, ...(thread.folder === "inbox" ? { tab: thread.tab } : {}) }));
  // The open reply box saves itself here; it stays open rather than lose text that couldn't be saved.
  const flushReply = useRef<(() => Promise<boolean>) | undefined>(undefined);
  // Moving it away closes it, with Undo on the toast (Gmail).
  const act = (fn: (id: string) => (() => void) | void, undoKey?: "mail.undo.archived" | "mail.undo.trashed" | "mail.undo.spam" | "mail.undo.inbox") => () => {
    void (flushReply.current?.() ?? Promise.resolve(true)).then((safe) => {
      if (!safe) return;
      const undo = fn(threadId);
      if (undoKey !== undefined && typeof undo === "function") showMailUndo(t(undoKey, { count: 1 }), undo);
      back();
    });
  };
  // A change made here shows at once (the tab tag, the star, labels) instead of waiting for the realtime echo.
  const shown = (patch: Partial<MailThread>) => {
    const cur = vm.state.thread;
    if (cur !== undefined) vm.onEvent({ type: "mail.thread.updated", thread: { ...cur, ...patch } });
  };
  const star = (on: boolean) => {
    mail.mailbox.setStarred(threadId, on);
    shown({ starred: on });
  };
  const setLabels = (ids: string[]) => {
    mail.mailbox.setLabels(threadId, ids);
    shown({ labelIds: ids });
  };
  const [replying, setReplying] = useState<{ kind: ReplyKind; start: ComposerStart; seq: number; nudge?: number } | undefined>(undefined);
  const [showAll, setShowAll] = useState(false);
  const [sentToast, setSentToast] = useState<SendMailResponse | undefined>(undefined);
  const opening = useRef(false);
  const reply = (kind: ReplyKind, messageId?: string) => {
    if (!canSend) return;
    // The quoted text may need loading: what's typed in that moment is the reply's, not a shortcut.
    if (opening.current) return;
    opening.current = true;
    const seq = ++replySeq;
    void vm
      .prepare(kind, messageId)
      .then((start) => {
        if (start === undefined) return;
        // The same message: its open box switches in place, what I wrote stays. Another one: the box's draft is kept.
        setReplying((cur) => (cur !== undefined && cur.start.sourceMessageId === start.sourceMessageId ? { ...cur, kind, nudge: (cur.nudge ?? 0) + 1 } : { kind: start.kind ?? kind, start, seq }));
      })
      .finally(() => {
        opening.current = false;
      });
  };
  const locale = useLocale();
  // "Propose new time": a reply to the organizer saying the time doesn't work (Gmail's for organizers outside Google).
  const propose = async (m: MailMessage) => {
    const start = await vm.prepare("reply", m.id, true);
    const inv = m.invite;
    if (start === undefined || inv === undefined) return;
    const when = inviteWhen(inv, locale, Intl.DateTimeFormat().resolvedOptions().timeZone);
    setReplying({
      kind: "reply",
      start: { ...start, to: [inv.organizer], cc: [], subject: t("mail.invite.proposeSubject", { title: inv.title }), text: `${t("mail.invite.proposeText", { time: when })}${start.text}` },
      seq: ++replySeq,
    });
  };

  // Coming back to a thread with a reply I left: the box is open again, as I left it (Gmail).
  const resumed = useRef(false);
  useEffect(() => {
    if (replying !== undefined) resumed.current = true;
  }, [replying]);
  // On a phone the box fills the screen, so the thread opens first and Reply brings the draft back.
  const phone = useMediaQuery("(max-width: 640px)");
  const resume = canSend && !phone && !resumed.current && replying === undefined && s.drafts.length > 0 ? vm.resumeStart() : undefined;
  useEffect(() => {
    if (resume === undefined) return;
    resumed.current = true;
    setReplying({ kind: resume.kind ?? "reply", start: resume, seq: ++replySeq });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resume?.draft?.id]);

  useMailShortcuts(nav.focused && thread !== undefined, (k) => {
    if (thread === undefined || !canSend) return false;
    if (opening.current) return true;
    if (k === "reply" || k === "replyAll" || k === "forward") {
      reply(k);
      return true;
    }
    if (k === "archive") act((id) => mail.mailbox.archive(id), "mail.undo.archived")();
    else if (k === "trash") act((id) => mail.mailbox.trash(id), "mail.undo.trashed")();
    else if (k === "spam") act((id) => mail.mailbox.markSpam(id, true), "mail.undo.spam")();
    else if (k === "star") star(!thread.starred);
    else if (k === "priority" || k === "notPriority") markPriority(t, mail.mailbox, [thread], k === "priority", false);
    else if (k === "markUnread") act((id) => mail.mailbox.setUnread(id, true))();
    else return false;
    return true;
  });

  if (s.status === "loading") {
    return (
      <Stack grow align="center" justify="center" testID="mail-thread-loading">
        <Spinner />
      </Stack>
    );
  }
  if (s.status === "failed" || thread === undefined) {
    return (
      <Stack grow align="center" justify="center" gap="3" padding="8" testID="mail-thread-failed">
        <Text color="text.secondary">{t("mail.loadFailed")}</Text>
        <Button variant="secondary" label={t("mail.retry")} onPress={() => void vm.load()} testID="mail-thread-retry" />
      </Stack>
    );
  }
  // Every category tab, its own one ticked (so the choice is never empty), like the list row's "Move to tab".
  const moveMenu: MenuEntry[] = Mail.categoryTabs(tabDefs).map((x) => ({
    id: `tab-${x.id}`,
    label: Mail.mailTabLabel(x, locale),
    icon: mailTabIcon(x),
    selected: x.id === thread.tab,
    onSelect: () => {
      if (x.id === thread.tab) return;
      mail.mailbox.moveToTab(threadId, x.id);
      shown({ tab: x.id });
    },
  }));
  const tabDef = tabDefs.find((x) => x.id === thread.tab);
  const inInbox = thread.folder === "inbox" || thread.folder === "sent";
  const codeView = Mail.mailCodeView(thread, ws.vm.deps.clock.now());
  const codeSender = s.messages.find((m) => m.direction === "in")?.from?.name ?? s.messages.find((m) => m.direction === "in")?.from?.address ?? "";
  return (
    <Stack grow testID="mail-thread">
      <Stack direction="horizontal" align="center" gap="1" paddingX="3" paddingY="2" wrap>
        <IconButton icon={nav.canSplit ? "close" : "arrowLeft"} accessibilityLabel={nav.canSplit ? t("common.close") : t("mail.back")} onPress={back} testID="mail-thread-back" />
        {/* Archive, spam, trash, star, snooze, labels: none while Email is look-only (Settings → Features). */}
        {canSend ? (
          <>
            {inInbox ? (
              <IconButton icon="archive" accessibilityLabel={t("mail.action.archive")} onPress={act((id) => mail.mailbox.archive(id), "mail.undo.archived")} testID="mail-thread-archive" />
            ) : (
              <IconButton icon="inbox" accessibilityLabel={t("mail.action.moveToInbox")} onPress={act((id) => mail.mailbox.moveToInbox(id), "mail.undo.inbox")} testID="mail-thread-inbox" />
            )}
            <IconButton icon="ban" accessibilityLabel={thread.folder === "spam" ? t("mail.action.notSpam") : t("mail.action.spam")} onPress={act((id) => mail.mailbox.markSpam(id, thread.folder !== "spam"), thread.folder !== "spam" ? "mail.undo.spam" : "mail.undo.inbox")} testID="mail-thread-spam" />
            <IconButton icon="trash" accessibilityLabel={t("mail.action.trash")} onPress={act((id) => mail.mailbox.trash(id), "mail.undo.trashed")} testID="mail-thread-trash" />
            <IconButton icon="mail" accessibilityLabel={t("mail.action.markUnread")} onPress={act((id) => mail.mailbox.setUnread(id, true))} testID="mail-thread-unread" />
            <IconButton
              icon={thread.starred ? "star" : "starOff"}
              accessibilityLabel={thread.starred ? t("mail.action.unstar") : t("mail.action.star")}
              onPress={() => star(!thread.starred)}
              testID="mail-thread-star"
            />
            <PriorityMarker t={t} thread={thread} now={ws.vm.deps.clock.now()} onToggle={(on) => markPriority(t, mail.mailbox, [thread], on, false)} testID="mail-thread-priority" />
            <Menu items={snoozeEntries(t, fmt.dateTime, ws.vm.deps.clock.now(), thread.snoozedUntil, (until) => mail.mailbox.snooze(threadId, until))} testID="mail-thread-snooze">
              <IconButton icon="clock" accessibilityLabel={t("mail.action.snooze")} onPress={() => undefined} testID="mail-thread-snooze-button" />
            </Menu>
            {labels.length > 0 ? (
              <Menu items={labelEntries(t, labels, thread.labelIds ?? [], setLabels)} testID="mail-thread-labels">
                <IconButton icon="tag" accessibilityLabel={t("mail.labels")} onPress={() => undefined} testID="mail-thread-labels-button" />
              </Menu>
            ) : null}
            <Menu items={moveMenu} testID="mail-thread-move">
              <IconButton icon="tabs" accessibilityLabel={t("mail.action.moveToTab")} onPress={() => undefined} testID="mail-thread-move-button" />
            </Menu>
          </>
        ) : null}
      </Stack>
      <Divider />
      <ScrollView padding="4" maxContentWidth="content.readable" testID="mail-thread-scroll">
        <Stack gap="3">
          <Stack direction="horizontal" align="center" gap="2" wrap>
            <Text variant="title2" weight="bold" selectable>
              {thread.subject === "" ? t("mail.noSubject") : thread.subject}
            </Text>
            {/* The tab tag opens the same All / Clients / Team choice as the toolbar's "Move to tab". */}
            {tabDef === undefined ? null : canSend && moveMenu.length > 0 ? (
              <Menu items={moveMenu} testID="mail-thread-tab-menu">
                <Pressable direction="horizontal" radius="full" onPress={() => undefined} accessibilityLabel={t("mail.action.moveToTab")} testID="mail-thread-tab">
                  <Chip label={Mail.mailTabLabel(tabDef, locale)} icon={mailTabIcon(tabDef)} />
                </Pressable>
              </Menu>
            ) : (
              <Chip label={Mail.mailTabLabel(tabDef, locale)} icon={mailTabIcon(tabDef)} />
            )}
            {/* A client in exactly one open project: that project (core threadProject). */}
            <ThreadProjectChip from={s.messages.find((m) => m.direction === "in")?.from} testID="mail-thread-project" />
            {thread.priority === "reminder" ? (
              <Chip label={t("mail.priority.reminder", { count: noReplyDays(thread, ws.vm.deps.clock.now()) })} tone="warning" icon="clock" />
            ) : thread.needsReply === true ? (
              <Chip label={t("mail.needsReply")} tone="warning" />
            ) : null}
            {labels
              .filter((l) => (thread.labelIds ?? []).includes(l.id))
              .map((l) => (
                <Chip key={l.id} label={l.name} tone={labelTone(l.color)} onRemove={() => setLabels((thread.labelIds ?? []).filter((x) => x !== l.id))} removeLabel={l.name} />
              ))}
            {thread.snoozedUntil !== undefined && thread.snoozedUntil > ws.vm.deps.clock.now() ? <Chip icon="clock" label={t("mail.snoozedUntil", { time: fmt.dateTime(thread.snoozedUntil) })} tone="info" /> : null}
          </Stack>
          {codeView === null ? null : (
            <MailCodeCard
              view={codeView}
              sender={codeSender}
              time={mailShortDate(thread.lastAt, locale)}
              onCopy={(text) => navigator.clipboard.writeText(text)}
              testID="mail-thread-code"
            />
          )}
          {s.suspicious ? <Banner tone="danger" icon="warning" message={t("mail.warning.suspicious")} testID="mail-thread-suspicious" /> : null}
          <AnimatedList>
          {s.messages.map((m, i) => {
            const n = s.messages.length;
            const folded = !showAll && n >= 5 && i > 0 && i < n - 2 && !s.expanded.includes(m.id);
            if (folded) {
              if (i !== 1) return null;
              const hidden = s.messages.slice(1, n - 2).filter((x) => !s.expanded.includes(x.id)).length;
              return (
                <FoldCount key="older" count={hidden} accessibilityLabel={t("mail.olderMessages", { count: hidden })} onPress={() => setShowAll(true)} testID="mail-thread-older" />
              );
            }
            return (
              <Stack key={m.id} background={s.newIds.includes(m.id) ? "accent.subtle" : undefined} fadeBackground radius="lg">
              <MessageCard
                m={m}
                onCancelSend={() =>
                  void mail.mailbox.undoSend(m.id).then((draft) => {
                    if (draft !== undefined) {
                      openComposer({ mailboxId: draft.mailboxId, mode: draft.mode, to: [], cc: [], subject: draft.subject, text: draft.text, draft });
                      void vm.load();
                    }
                  })
                }
                expanded={s.expanded.includes(m.id)}
                body={s.bodies[m.id]}
                onToggle={() => vm.toggle(m.id)}
                onLoadImages={() => vm.loadImages(m.id)}
                onToggleView={() => vm.toggleView(m.id)}
                onReply={canSend ? (kind) => reply(kind, m.id) : undefined}
                attachmentState={downloads.state}
                onAttachment={(a, intent) => downloads.start(m.id, a, intent)}
                answering={s.answering[m.id]}
                answerFailed={s.answerFailed === m.id}
                onAnswer={canSend ? (response, scope) => void vm.answerInvite(m.id, response, scope) : undefined}
                onPropose={canSend ? () => void propose(m) : undefined}
              />
              </Stack>
            );
          })}
          </AnimatedList>
          {s.textFailed === true ? (
            <Text color="status.danger.fg" variant="footnote" testID="mail-thread-text-failed">
              {t("mail.thread.textFailed")}
            </Text>
          ) : null}
          {replying === undefined ? null : (
            <MailReplyBox
              key={replying.seq}
              seq={replying.seq}
              start={replying.start}
              kind={replying.kind}
              nudge={replying.nudge ?? 0}
              onKind={(kind) => setReplying({ ...replying, kind })}
              onLeave={(source, draft) => vm.noteDraft(source, draft)}
              mail={mail}
              flushRef={flushReply}
              onClose={() => setReplying(undefined)}
              onSent={(sent) => {
                setReplying(undefined);
                setSentToast(sent);
                void vm.load();
              }}
            />
          )}
        </Stack>
      </ScrollView>
      {canSend && replying === undefined ? (
        <>
          <Divider />
          <Stack direction="horizontal" gap="2" wrap justify="center" paddingX="4" paddingY="2" testID="mail-reply-bar">
            <Button variant="secondary" icon="reply" label={t("mail.action.reply")} onPress={() => reply("reply")} testID="mail-reply" />
            <Button variant="secondary" icon="users" label={t("mail.action.replyAll")} onPress={() => reply("replyAll")} testID="mail-reply-all" />
            <Button variant="secondary" icon="arrowRight" label={t("mail.action.forward")} onPress={() => reply("forward")} testID="mail-forward" />
          </Stack>
        </>
      ) : null}
      <SentToast sent={sentToast} mail={mail} onClose={() => setSentToast(undefined)} />
      {downloads.toast}
    </Stack>
  );
}

type CardProps = {
  m: MailMessage;
  expanded: boolean;
  body: MailBody | undefined;
  onToggle: () => void;
  onLoadImages: () => void;
  /** Reading view ⇄ the original email (when it has a reading view). */
  onToggleView: () => void;
  /** Absent while Email is look-only (Settings → Features). */
  onReply?: ((kind: ReplyKind) => void) | undefined;
  /** A chip's press opens it, its download button only saves it. */
  onAttachment: (attachment: DownloadableAttachment, intent: AttachmentIntent) => void;
  attachmentState: (attachmentId: string) => AttachmentState | undefined;
  /** A scheduled message (or one in its undo window): cancel it back into a draft. */
  onCancelSend: () => void;
  /** Its calendar invite: my answer on its way, whether it failed, answering it and "Propose new time". */
  answering: "accepted" | "declined" | "tentative" | undefined;
  answerFailed: boolean;
  onAnswer: ((response: "accepted" | "declined" | "tentative", scope?: "series" | "occurrence") => void) | undefined;
  onPropose: (() => void) | undefined;
};

function MessageCard({ m, expanded, body, onToggle, onLoadImages, onToggleView, onReply, onAttachment, attachmentState, onCancelSend, answering, answerFailed, onAnswer, onPropose }: CardProps) {
  const ws = useWorkspace();
  const t = useT();
  const fmt = useFormat();
  const locale = useLocale();
  const sender = Mail.displayName(m.from);
  const to = [...m.to, ...m.cc].map(Mail.displayName).join(", ");
  // "to {names}" with each name a PersonName (its card); the sentence's words around the names stay translated.
  const [toBefore = "", toAfter = ""] = t("mail.to", { names: "\u0000" }).split("\u0000");
  const recipients = [...m.to, ...m.cc, ...(m.bcc ?? [])];
  const auth = m.auth;
  const verified = auth !== undefined && auth.dmarc === "pass";
  const failed = auth !== undefined && (auth.dmarc === "fail" || auth.spf === "fail");
  // With an invite card, the calendar part sent alongside the .ics (Gmail's text/calendar alternative) isn't listed.
  const files = m.attachments.filter((a) => !a.inline && !(m.invite !== undefined && /^text\/calendar/i.test(a.contentType) && !/\.ics$/i.test(a.filename)));
  const [showQuote, setShowQuote] = useState(false);
  const split = Mail.splitQuote(m.text);
  const replyMenu: MenuEntry[] =
    onReply === undefined
      ? []
      : [
          { id: "reply", label: t("mail.action.reply"), icon: "reply", onSelect: () => onReply("reply") },
          { id: "reply-all", label: t("mail.action.replyAll"), icon: "users", onSelect: () => onReply("replyAll") },
          { id: "forward", label: t("mail.action.forward"), icon: "arrowRight", onSelect: () => onReply("forward") },
        ];
  return (
    <Card padding="3" gap="3" testID={`mail-message-${m.id}`}>
      <Stack direction="horizontal" align="start" gap="3">
        <Avatar name={sender} src={Mail.mailAvatarPath(ws.vm.wsId, m.from.address)} size="avatar.md" />
        <Stack grow>
        <Pressable direction="vertical" onPress={onToggle} accessibilityLabel={sender} testID={`mail-message-toggle-${m.id}`}>
          {/* Not grow: in the vertical Pressable a growing child collapses to 0 high and the "to" line overlapped the body. */}
          <Stack gap="0.5">
            <Stack direction="horizontal" align="center" gap="2" wrap>
              {expanded ? (
                <PersonName address={m.from} text={sender} strong testID={`mail-person-from-${m.id}`} />
              ) : (
                <Text weight="semibold" lines={1}>
                  {sender}
                </Text>
              )}
              {expanded ? <PersonName address={m.from} text={`<${m.from.address}>`} variant="footnote" muted testID={`mail-person-address-${m.id}`} /> : null}
              {expanded && verified ? <Chip label={t("mail.auth.pass")} tone="success" icon="shield" /> : null}
              {expanded && failed ? <Chip label={t("mail.auth.fail")} tone="danger" icon="warning" /> : null}
              {m.via === undefined ? null : <Chip label={t("mail.thread.via", { address: m.via })} icon="users" testID={`mail-via-${m.id}`} />}
              {m.status === "queued" ? <Chip label={t("mail.status.queued")} tone="info" /> : null}
              {m.status === "scheduled" && m.sendAt !== undefined ? <Chip icon="clock" label={t("mail.scheduledFor", { time: fmt.dateTime(m.sendAt) })} tone="info" /> : null}
              {m.status === "scheduled" ? <Button variant="secondary" size="sm" label={t("mail.action.cancelSend")} onPress={onCancelSend} testID={`mail-cancel-send-${m.id}`} /> : null}
              {m.status === "failed" ? <Chip label={t("mail.status.failed")} tone="danger" /> : null}
              {m.status === "bounced" ? <Chip label={t("mail.status.bounced")} tone="danger" /> : null}
            </Stack>
            {expanded && recipients.length > 0 ? (
              <Stack direction="horizontal" align="center" gap="1" wrap>
                {toBefore.trim() === "" ? null : (
                  <Text variant="footnote" color="text.secondary">
                    {toBefore.trim()}
                  </Text>
                )}
                {recipients.map((a, i) => (
                  <Stack key={`${a.address}-${i}`} direction="horizontal" align="center">
                    <PersonName address={a} variant="footnote" muted testID={`mail-person-to-${m.id}-${i}`} />
                    {i < recipients.length - 1 ? (
                      <Text variant="footnote" color="text.secondary">
                        {","}
                      </Text>
                    ) : null}
                  </Stack>
                ))}
                {toAfter.trim() === "" ? null : (
                  <Text variant="footnote" color="text.secondary">
                    {toAfter.trim()}
                  </Text>
                )}
              </Stack>
            ) : (
              <Text variant="footnote" color="text.secondary" lines={1}>
                {expanded ? t("mail.to", { names: to === "" ? t("mail.me") : to }) : Mail.snippetOf(m.text)}
              </Text>
            )}
          </Stack>
        </Pressable>
        </Stack>
        <Stack direction="horizontal" align="center" gap="1">
          {files.length > 0 ? <Icon name="attach" size="icon.sm" color="text.tertiary" /> : null}
          <Tooltip label={fmt.dateTime(m.sentAt)}>
            <Text variant="footnote" color="text.secondary">
              {mailShortDate(m.sentAt, locale)}
            </Text>
          </Tooltip>
          {expanded && replyMenu.length > 0 ? (
            <Menu items={replyMenu} align="end" testID={`mail-message-menu-${m.id}`}>
              <IconButton icon="more" size="sm" accessibilityLabel={t("mail.more")} onPress={() => undefined} testID={`mail-message-more-${m.id}`} />
            </Menu>
          ) : null}
        </Stack>
      </Stack>
      {expanded ? (
        <Stack gap="3">
          {m.invite !== undefined ? (
            <MailInviteCard
              messageId={m.id}
              invite={m.invite}
              answering={answering}
              failed={answerFailed}
              onAnswer={onAnswer}
              onPropose={onPropose}
              onDownload={() => onAttachment(m.attachments.find((a) => a.id === m.invite!.attachmentId) ?? { id: m.invite!.attachmentId, filename: "invite.ics", contentType: "text/calendar" }, "download")}
            />
          ) : null}
          {m.remoteImages && body?.imagesLoaded !== true ? (
            <Banner
              tone="neutral"
              icon="eyeOff"
              message={t("mail.imagesBlocked")}
              action={{ label: t("mail.action.loadImages"), onPress: onLoadImages, testID: `mail-load-images-${m.id}` }}
              testID={`mail-images-blocked-${m.id}`}
            />
          ) : null}
          {!m.hasHtml || body?.status === "failed" || (body?.status === "ready" && body.html === undefined) ? (
            <Stack gap="2">
              <Text selectable>{showQuote || split.quote === "" ? m.text : split.body}</Text>
              {showQuote || split.quote === "" ? null : <QuoteToggle onPress={() => setShowQuote(true)} testID={`mail-show-quote-${m.id}`} />}
            </Stack>
          ) : body === undefined || body.status === "loading" || body.html === undefined ? (
            <Spinner size="icon.sm" />
          ) : (
            <Stack gap="1.5">
              {body.canRead ? (
                <Stack direction="horizontal" justify="end">
                  <Button
                    variant="ghost"
                    size="sm"
                    icon="page"
                    label={body.view === "readable" ? t("mail.view.original") : t("mail.view.reading")}
                    onPress={onToggleView}
                    testID={`mail-toggle-view-${m.id}`}
                  />
                </Stack>
              ) : null}
              <HtmlFrame html={body.html} title={m.subject} testID={`mail-frame-${m.id}`} />
            </Stack>
          )}
          {files.length > 0 ? (
            <Stack direction="horizontal" gap="2" wrap>
              {files.map((a) => {
                const state = attachmentState(a.id);
                const busy = state?.phase === "busy";
                const percent = busy && state.progress !== undefined ? ` · ${new Intl.NumberFormat(locale, { style: "percent" }).format(state.progress)}` : "";
                return (
                  <Chip
                    key={a.id}
                    icon={state?.phase === "done" ? "check" : (Files.fileIcon(Files.fileKind(a.contentType, a.filename)) as IconName)}
                    loading={busy}
                    loadingLabel={t(state?.intent === "download" ? "mail.attachment.downloading" : "mail.attachment.opening", { name: a.filename })}
                    label={busy ? `${a.filename}${percent}` : `${a.filename} · ${mailFileSize(a.size, locale)}`}
                    accessibilityLabel={t("mail.attachment.openNamed", { name: a.filename })}
                    onPress={() => onAttachment(a, "open")}
                    trailingAction={{ icon: "download", label: t("mail.attachment.downloadNamed", { name: a.filename }), onPress: () => onAttachment(a, "download"), testID: `mail-attachment-${a.id}-download` }}
                    testID={`mail-attachment-${a.id}`}
                  />
                );
              })}
            </Stack>
          ) : null}
        </Stack>
      ) : null}
    </Card>
  );
}
